import dayjs from 'dayjs';
import { OrgOverviewReleaseTypesFilter } from '@/types';
import { MIN_ORG_METRIC_PERIOD_IN_DAYS } from './OrgOverviewDatePickerTimeRange';
import { OrgNavigationDateRangeType } from './OrgNavigationDateRange';
import { OrgNavigationFilters } from './OrgNavigationContent';

const ORG_OVERVIEW_FILTERS_STORAGE_KEY = 'org-overview-filters';

export const hasStoredOrgNavigationFilters = (): boolean => {
    try {
        return !!window.localStorage.getItem(ORG_OVERVIEW_FILTERS_STORAGE_KEY);
    } catch {
        return false;
    }
};

export const toSerializableOrgNavigationFilters = (filters: OrgNavigationFilters) => {
    return {
        selectedDateRangeType: filters.selectedDateRangeType,
        selectedDateRange: filters.selectedDateRange,
        selectedMetrics: filters.selectedMetrics,
        selectedWorkflowApps: filters.selectedWorkflowApps,
        selectedDestinations: filters.selectedDestinations,
        selectedPlatforms: filters.selectedPlatforms,
        selectedUsers: filters.selectedUsers,
        selectedReleaseTypes: filters.selectedReleaseTypes,
        shouldGenerateInsights: filters.shouldGenerateInsights,
    };
};

export const createDefaultOrgNavigationFilters = (): OrgNavigationFilters => {
    const startDate = dayjs().subtract(MIN_ORG_METRIC_PERIOD_IN_DAYS, 'days');
    const endDate = dayjs();

    return {
        selectedDateRangeType: OrgNavigationDateRangeType.Custom,
        selectedDateRange: {
            start: startDate.format('YYYY-MM-DD'),
            end: endDate.format('YYYY-MM-DD'),
        },
        selectedMetrics: [],
        selectedWorkflowApps: {},
        selectedDestinations: {},
        selectedPlatforms: {},
        selectedUsers: {},
        shouldGenerateInsights: false,
        selectedReleaseTypes: OrgOverviewReleaseTypesFilter.AllReleaseTypes,
    };
};

const parseStoredOrgOverviewFilters = (value: string): OrgNavigationFilters | null => {
    if (!value) {
        return null;
    }

    try {
        return JSON.parse(value) as OrgNavigationFilters;
    } catch {
        return null;
    }
};

export const getStoredOrgNavigationFilters = (): OrgNavigationFilters | null => {
    try {
        const storedFilters = window.localStorage.getItem(ORG_OVERVIEW_FILTERS_STORAGE_KEY) || '';
        const parsedFilters = parseStoredOrgOverviewFilters(storedFilters) || ({} as OrgNavigationFilters);
        const defaultFilters = createDefaultOrgNavigationFilters();

        return {
            ...defaultFilters,
            ...parsedFilters,
        };
    } catch {
        return null;
    }
};

export const setStoredOrgNavigationFilters = (filters: OrgNavigationFilters): void => {
    try {
        window.localStorage.setItem(
            ORG_OVERVIEW_FILTERS_STORAGE_KEY,
            JSON.stringify(toSerializableOrgNavigationFilters(filters)),
        );
    } catch {
        return;
    }
};
